import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useCampaignMutation } from "./useCampaignMutation";
import type { CampaignGift, GiftPatch, TierCode, TierWithGifts } from "../../lib/types";

export const useTiers = (campaignId: string | undefined) =>
	useQuery({
		queryKey: ["tiers", campaignId],
		queryFn: () => api.get<{ items: TierWithGifts[] }>(`/api/campaigns/${campaignId}/tiers`),
		enabled: Boolean(campaignId),
	});

/**
 * A gift is read by the tier list and again by the editor open inside it.
 * Refreshing only the first leaves the editor showing the value the merchant
 * just changed away from, so its controls snap back.
 */
const useTierMutation = <TVariables>(
	campaignId: string,
	run: (variables: TVariables) => Promise<unknown>,
) => useCampaignMutation(campaignId, [["tiers", campaignId], ["gift-options"]], run);

export const useCreateTier = (campaignId: string) =>
	useTierMutation(campaignId, (thresholdMinor: number) =>
		api.post(`/api/campaigns/${campaignId}/tiers`, { thresholdMinor }),
	);

export const useUpdateTier = (campaignId: string) =>
	useTierMutation(
		campaignId,
		({ tierId, thresholdMinor }: { tierId: string; thresholdMinor: number }) =>
			api.put(`/api/campaigns/${campaignId}/tiers/${tierId}`, { thresholdMinor }),
	);

export interface TierFallbackInput {
	productId: string;
	variantId: string | null;
	selectVariant: boolean;
}

export const useSetTierFallback = (campaignId: string) =>
	useTierMutation(
		campaignId,
		({ tierId, fallback }: { tierId: string; fallback: TierFallbackInput | null }) =>
			api.put(`/api/campaigns/${campaignId}/tiers/${tierId}`, { fallback }),
	);

export const useSaveTierCode = (campaignId: string) =>
	useTierMutation(campaignId, ({ tierId, ...code }: { tierId: string } & TierCode) =>
		api.put(`/api/campaigns/${campaignId}/tiers/${tierId}/code`, code),
	);

export const useDeleteTier = (campaignId: string) =>
	useTierMutation(campaignId, (tierId: string) =>
		api.remove(`/api/campaigns/${campaignId}/tiers/${tierId}`),
	);

export const useCreateGift = (campaignId: string) =>
	useTierMutation(campaignId, ({ tierId, ...gift }: { tierId: string } & Partial<CampaignGift>) =>
		api.post(`/api/campaigns/${campaignId}/tiers/${tierId}/gifts`, gift),
	);

export const useUpdateGift = (campaignId: string) =>
	useTierMutation(campaignId, ({ giftId, ...patch }: { giftId: string } & GiftPatch) =>
		api.put(`/api/campaigns/${campaignId}/gifts/${giftId}`, patch),
	);

export const useDeleteGift = (campaignId: string) =>
	useTierMutation(campaignId, (giftId: string) =>
		api.remove(`/api/campaigns/${campaignId}/gifts/${giftId}`),
	);
